import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useState, type ReactNode } from "react";

import { useAppAuth } from "./auth";
import { trpc } from "./trpc";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const DEV_USER_ID = "dev-user";

interface TrpcProviderProps {
  children: ReactNode;
}

/**
 * Wires the tRPC client to {@link useAppAuth}: a Clerk session token is
 * sent as a Bearer header when one is available; in dev mode (where
 * `getToken()` always resolves `null`), the `x-dev-user` header is sent
 * instead, matching the backend's `AUTH_MODE=dev` shortcut
 * (`projects/hindi/backend/src/context.ts`).
 */
export function TrpcProvider({ children }: TrpcProviderProps) {
  const { mode, getToken } = useAppAuth();
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_BASE}/trpc`,
          async headers() {
            const token = await getToken();
            if (token) return { Authorization: `Bearer ${token}` };
            if (mode === "dev") return { "x-dev-user": DEV_USER_ID };
            return {};
          },
        }),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
